// The paper trail's all-time record, without keeping the trail itself forever.
//
// `prefs.proposalOutcomes` holds sixty days of settled proposals, every leg of
// every one of them, and it is capped there on purpose: it is a phone's
// localStorage, and a year of pads in it would be megabytes of JSON re-parsed
// on every read. But Insights wants to say what the pad has done ALL TIME, and
// a number that resets every sixty days is not that.
//
// So the trail is folded down as it is written. Each day becomes a handful of
// counters per strategy — `Agg` below — which is two orders of magnitude
// smaller than the day it came from and answers every question Insights
// actually asks of it. Days older than 120 are folded again into their month
// and the day buckets dropped, so the file stops growing in daily rows and
// grows in monthly ones instead: twelve a year.
//
// THE ONE RULE THAT MAKES THIS SAFE: a live day never gets special arithmetic.
// `paperDays` puts every day it can see — rolled-up or still in the trail —
// through `foldDay`, so "the last 7 days" and "all time" are added up by the
// same code over the same shape. Two counting paths for one number is how a
// screen ends up disagreeing with itself, and the disagreement always shows up
// on the day the window crosses the boundary.
//
// Pure but for `readRollup`, which is one localStorage read.

import { prefs } from '../core/storage.js';
import { meanInterval } from './engine/metrics.js';
import { shiftDate } from './paper-trail.js';
import { canonicalStrategyId } from './engine/strategies/aliases.js';
import { sportCodes } from './history-view.js';

/** Where the rollup lives. `prefs.proposalRollup`. */
export const ROLLUP_KEY = 'proposalRollup';

/**
 * The stored shape. v2 adds `q2` — the sum of the squares of those legs' CLV,
 * which is what an interval on the mean needs and a sum alone cannot give.
 *
 * A v1 rollup is UPGRADED rather than discarded (see `upgrade`): its money is
 * every bit as true as it was, and throwing it away would cost a punter their
 * all-time record to fix an arithmetic they never saw. What does not survive is
 * the CLV counters: the legs behind them were measured against the bet's own
 * book (engine review #6), they were folded down months ago and cannot be
 * re-derived, and carrying them forward would mean an "all time" CLV that is
 * two different measurements added together.
 */
export const ROLLUP_VERSION = 2;

/**
 * How many days stay as days. Beyond this they are folded into months.
 * Twice the trail's own 60, so a day is only ever folded once its source is
 * long gone and cannot be re-folded on top of itself.
 */
export const KEEP_DAYS = 120;

/** Classic is the only strategy split into its tiers. Same literal as `CLASSIC.id`. */
export const CLASSIC_KEY = 'classic-four-ways';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_RE = /^\d{4}-\d{2}$/;

const cents = (n) => Math.round(n * 100) / 100;
/** CLV sums are floats; rounding them keeps the stored JSON byte-stable. */
const r8 = (n) => Math.round(n * 1e8) / 1e8;

/**
 * The counters, and what each letter is. Short names because this is stored
 * JSON on a phone and the keys are most of the bytes:
 *
 *   p  proposed            g  legs
 *   d  decided             c  legs with a measurable close
 *   w  won                 q  Σ those legs' CLV
 *   l  lost                q2 Σ those legs' CLV squared
 *   v  void                sp per sport: `[g, w, l, v, c, q]`, the same six
 *   s  staked (decided)       counted over LEGS rather than tickets
 *   r  returned (decided)
 *
 * Staked and returned count DECIDED tickets only, exactly as `summariseDay`
 * does — a ticket still waiting on a Sunday night game has a stake and no
 * return, and carrying it would print a loss the pad has not taken.
 *
 * `q2` is the whole reason a folded record can still print an interval: mean
 * and spread both come out of three running totals, so "all time" is as
 * measurable as "last seven days" without keeping a year of legs on a phone.
 * The sport rows carry no square and so print no interval — a sport is a way of
 * reading the trail, not a claim the app makes.
 */
export const emptyAgg = () => ({ p: 0, d: 0, w: 0, l: 0, v: 0, s: 0, r: 0, g: 0, c: 0, q: 0, q2: 0, sp: {} });

/**
 * The bucket a settled proposal belongs in: its strategy id, except Classic,
 * which is four strategies wearing one name and is split by tier.
 *
 * Canonicalised, the same as `insights.js`'s `keyFor` and for the same reason:
 * a proposal dealt under a retired id settles into its heir's bucket. Days
 * already folded into a month keep the key they were written under — they are
 * arithmetic now, not proposals, and re-keying them would mean re-folding a
 * trail that has been pruned.
 */
export function keyFor(proposal, classicId = CLASSIC_KEY) {
  const id = canonicalStrategyId(proposal?.strategyId ?? proposal?.multi?.strategyId ?? null);
  if (!id) return null;
  if (id !== classicId) return String(id);
  const tier = proposal?.multi?.tier ?? null;
  return tier ? `${id}#${tier}` : String(id);
}

/** A key read back: `{ strategyId, tier }`. `tier` is null off Classic. */
export function splitKey(key) {
  const raw = String(key ?? '');
  const hash = raw.indexOf('#');
  if (hash < 0) return { strategyId: raw || null, tier: null };
  return { strategyId: raw.slice(0, hash) || null, tier: raw.slice(hash + 1) || null };
}

function addProposal(agg, proposal) {
  agg.p += 1;

  const outcome = proposal?.outcome;
  if (outcome && outcome !== 'pending') {
    agg.d += 1;
    if (outcome === 'won') agg.w += 1;
    else if (outcome === 'lost') agg.l += 1;
    else if (outcome === 'void') agg.v += 1;
    agg.s = cents(agg.s + (Number(proposal.staked) || 0));
    agg.r = cents(agg.r + (Number(proposal.returned) || 0));
  }

  for (const leg of proposal?.legs ?? []) {
    agg.g += 1;
    // The SOURCE, not the closing price: a leg counts toward CLV only once it
    // has been measured against the exchange or against the board without its
    // own book (engine review #6, `closingFairFor`). `summariseDay` counts the
    // same legs by the same test, which is what keeps the live day and the
    // folded one saying the same number.
    const closed = !!leg?.clvSource && Number.isFinite(leg?.clv);
    if (closed) {
      agg.c += 1;
      agg.q = r8(agg.q + leg.clv);
      agg.q2 = r8(agg.q2 + leg.clv * leg.clv);
    }

    const code = sportCodes([leg])[0] ?? null;
    if (!code) continue;
    const row = (agg.sp[code] ??= [0, 0, 0, 0, 0, 0]);
    row[0] += 1;
    if (leg?.result === 'won') row[1] += 1;
    else if (leg?.result === 'lost') row[2] += 1;
    else if (leg?.result === 'void') row[3] += 1;
    if (closed) {
      row[4] += 1;
      row[5] = r8(row[5] + leg.clv);
    }
  }
}

/**
 * One day of settled proposals, folded: `{ [key]: Agg }`.
 *
 * The ONLY way a day ever enters the arithmetic, live or stored. A proposal
 * with no strategy on it is dropped rather than bucketed under a blank key —
 * it predates strategies, and it is not evidence about any of them.
 */
export function foldDay(settled, { classicId = CLASSIC_KEY } = {}) {
  const out = {};
  for (const proposal of Array.isArray(settled) ? settled : []) {
    const key = keyFor(proposal, classicId);
    if (!key) continue;
    addProposal((out[key] ??= emptyAgg()), proposal);
  }
  return out;
}

/** Two `Agg`s, added. Neither argument is touched. */
export function mergeAgg(a, b) {
  const out = emptyAgg();
  for (const k of ['p', 'd', 'w', 'l', 'v', 'g', 'c']) out[k] = (Number(a?.[k]) || 0) + (Number(b?.[k]) || 0);
  out.s = cents((Number(a?.s) || 0) + (Number(b?.s) || 0));
  out.r = cents((Number(a?.r) || 0) + (Number(b?.r) || 0));
  out.q = r8((Number(a?.q) || 0) + (Number(b?.q) || 0));
  out.q2 = r8((Number(a?.q2) || 0) + (Number(b?.q2) || 0));
  for (const source of [a?.sp, b?.sp]) {
    for (const [code, row] of Object.entries(source ?? {})) {
      const cur = (out.sp[code] ??= [0, 0, 0, 0, 0, 0]);
      for (let i = 0; i < 6; i += 1) cur[i] += Number(row?.[i]) || 0;
      cur[5] = r8(cur[5]);
    }
  }
  return out;
}

/** Two `{ [key]: Agg }` maps, added key by key. */
export function mergeByKey(a, b) {
  const out = {};
  for (const [key, agg] of Object.entries(a ?? {})) out[key] = mergeAgg(emptyAgg(), agg);
  for (const [key, agg] of Object.entries(b ?? {})) out[key] = mergeAgg(out[key], agg);
  return out;
}

/**
 * An `Agg` said out loud. What every screen reads; nothing outside this file
 * should be indexing single letters.
 *
 * THE LONG NAMES ARE `lib/insights.js`'s, not a second opinion about them. Its
 * `emptyAgg`/`mergeAgg`/`totalAgg`/`sportRows` all work over this shape, so an
 * expanded rollup can be handed straight to `paperSummary` and `scoreboardRows`
 * — `stores/insights.js` maps `paperByKey`'s output through here and nothing
 * else has to know the stored form exists. `sports` is a map for the same
 * reason: `sportRows` indexes it by code.
 *
 * `avgClv`, `strike` and `roi` are the derived extras on top, null rather than
 * zero when there is nothing to divide by — a pad with no closing prices has
 * not got a CLV of nothing.
 */
export function expandAgg(agg) {
  const a = agg ?? emptyAgg();
  const staked = cents(Number(a.s) || 0);
  const returned = cents(Number(a.r) || 0);
  const proposed = Number(a.p) || 0;
  const decided = Number(a.d) || 0;
  const won = Number(a.w) || 0;
  const withClose = Number(a.c) || 0;
  const clvSum = Number(a.q) || 0;
  const clvSqSum = Number(a.q2) || 0;

  const sports = {};
  for (const [code, row] of Object.entries(a.sp ?? {})) {
    sports[code] = {
      legs: Number(row?.[0]) || 0,
      won: Number(row?.[1]) || 0,
      lost: Number(row?.[2]) || 0,
      void: Number(row?.[3]) || 0,
      legsWithClose: Number(row?.[4]) || 0,
      clvSum: Number(row?.[5]) || 0,
    };
  }

  return {
    proposed,
    decided,
    won,
    lost: Number(a.l) || 0,
    void: Number(a.v) || 0,
    pending: Math.max(0, proposed - decided),
    staked,
    returned,
    legs: Number(a.g) || 0,
    legsWithClose: withClose,
    clvSum,
    clvSqSum,
    sports,
    net: cents(returned - staked),
    roi: staked > 0 ? (returned - staked) / staked : null,
    strike: decided ? won / decided : null,
    avgClv: withClose ? clvSum / withClose : null,
    clvInterval: meanInterval({ n: withClose, sum: clvSum, sumSq: clvSqSum }),
  };
}

/** Every key in a `paperByKey` answer, expanded. The form `lib/insights.js` eats. */
export const expandByKey = (byKey) =>
  Object.fromEntries(Object.entries(byKey ?? {}).map(([key, agg]) => [key, expandAgg(agg)]));

/** An empty rollup, and the shape everything below assumes. */
export const emptyRollup = () => ({ v: ROLLUP_VERSION, days: {}, months: {} });

/**
 * A v1 bucket brought to v2: the money and the counts kept, the CLV counters
 * dropped.
 *
 * Dropped rather than carried because a v1 `q` is a sum of own-book CLVs
 * (engine review #6) and there is nothing left to re-derive them from — the
 * legs were folded away when the day was. Zero legs with a close is the honest
 * reading of a bucket whose closes were measured against the wrong thing: the
 * figure comes back as those days age out, and nothing in between is a blend of
 * two arithmetics.
 */
const upgradeAgg = (agg) => ({ ...mergeAgg(emptyAgg(), agg), c: 0, q: 0, q2: 0 });

const upgradeByKey = (byKey) =>
  Object.fromEntries(Object.entries(byKey ?? {}).map(([key, agg]) => [key, upgradeAgg(agg)]));

const upgradeBuckets = (buckets) =>
  Object.fromEntries(Object.entries(buckets ?? {}).map(([at, byKey]) => [at, upgradeByKey(byKey)]));

/**
 * Whatever is in storage, made safe to index. A v1 rollup is upgraded in place
 * (see `ROLLUP_VERSION`); anything older or stranger than that is not a rollup
 * this file wrote, and is discarded.
 */
export function readRollup() {
  const raw = prefs[ROLLUP_KEY];
  if (!raw || typeof raw !== 'object') return emptyRollup();
  const days = raw.days && typeof raw.days === 'object' ? raw.days : {};
  const months = raw.months && typeof raw.months === 'object' ? raw.months : {};
  if (raw.v === 1) {
    return { v: ROLLUP_VERSION, days: upgradeBuckets(days), months: upgradeBuckets(months) };
  }
  if (raw.v !== ROLLUP_VERSION) return emptyRollup();
  return { v: ROLLUP_VERSION, days, months };
}

/**
 * Days older than `keepDays`, folded into their months and dropped.
 *
 * Idempotent because the fold and the delete are the same step: a day that has
 * been folded is not there to fold again. This is also why `rollupFrom` refuses
 * to write a day older than the cutoff — re-folding one that had already been
 * folded is the one way this arithmetic could double-count.
 */
export function foldToMonths(rollup, today, { keepDays = KEEP_DAYS } = {}) {
  const days = { ...(rollup?.days ?? {}) };
  const months = { ...(rollup?.months ?? {}) };
  const cutoff = shiftDate(today, -keepDays);

  for (const date of Object.keys(days).sort()) {
    if (!DATE_RE.test(date)) {
      delete days[date];
      continue;
    }
    if (date >= cutoff) continue;
    const month = date.slice(0, 7);
    months[month] = mergeByKey(months[month], days[date]);
    delete days[date];
  }

  return { v: ROLLUP_VERSION, days, months };
}

/**
 * The rollup after a settle: every day the trail still holds, re-folded.
 *
 * LIVE WINS, ALWAYS. A day that is still in `proposalOutcomes` is re-folded
 * from scratch and overwrites whatever the rollup had for it, so a late score,
 * a hand-marked leg or a closing price that only arrived this morning corrects
 * the record instead of being added to it. That is what makes this idempotent:
 * running it twice on the same trail writes the same numbers, because the
 * second run replaces rather than accumulates.
 *
 * @param {object|null} prev            the stored rollup, or null on the first run
 * @param {object} outcomesByDate       `{ 'YYYY-MM-DD': [settled proposals] }`
 * @param {string} today                the Sydney day, 'YYYY-MM-DD'
 */
export function rollupFrom(prev, outcomesByDate, today, { keepDays = KEEP_DAYS, classicId = CLASSIC_KEY } = {}) {
  const base = prev && prev.v === ROLLUP_VERSION ? prev : emptyRollup();
  const days = { ...(base.days ?? {}) };
  const cutoff = shiftDate(today, -keepDays);

  for (const [date, list] of Object.entries(outcomesByDate ?? {})) {
    if (!DATE_RE.test(date)) continue;
    // Already folded into a month, or not a day yet. Either way it is not the
    // trail's to write.
    if (date < cutoff || date > today) continue;
    const folded = foldDay(list, { classicId });
    if (Object.keys(folded).length) days[date] = folded;
    else delete days[date];
  }

  return foldToMonths({ v: ROLLUP_VERSION, days, months: base.months ?? {} }, today, { keepDays });
}

const inWindow = (date, from, to) => (from == null || date >= from) && (to == null || date <= to);

/** The last day of a 'YYYY-MM' month, as 'YYYY-MM-DD'. */
function monthEnd(month) {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}

/**
 * Every day in the window, folded: `{ 'YYYY-MM-DD': { [key]: Agg } }`.
 *
 * `outcomes` is the live trail (`stores/paper-trail.js`'s `outcomes.value`) and
 * `rollup` is what `readRollup` returns. The live copy is folded LAST so it
 * wins over the stored fold of the same day, which is the same rule
 * `rollupFrom` writes by.
 *
 * @param {object} window   `{ from, to }` Sydney days; `from: null` is all time
 */
export function paperDays(window = {}, { outcomes = {}, rollup = null, classicId = CLASSIC_KEY } = {}) {
  const from = window?.from ?? null;
  const to = window?.to ?? null;
  const out = {};

  for (const [date, byKey] of Object.entries(rollup?.days ?? {})) {
    if (!DATE_RE.test(date) || !inWindow(date, from, to)) continue;
    out[date] = mergeByKey(byKey, null);
  }
  for (const [date, list] of Object.entries(outcomes ?? {})) {
    if (!DATE_RE.test(date) || !inWindow(date, from, to)) continue;
    out[date] = foldDay(list, { classicId });
  }
  return out;
}

/**
 * The window's paper record, per strategy key: `{ [key]: Agg }`.
 *
 * Days first, then whichever months sit ENTIRELY inside the window. A month
 * bucket only exists once its days were dropped, so it can never overlap a day
 * bucket; and a month only half inside the window is left out rather than
 * apportioned, because a rollup cannot be cut in half and a guess at the split
 * is worse than a smaller honest number. In practice this costs nothing: months
 * only hold days older than 120, and the only window that reaches them is All.
 */
export function paperByKey(window = {}, sources = {}) {
  const from = window?.from ?? null;
  const to = window?.to ?? null;
  let out = {};

  for (const byKey of Object.values(paperDays(window, sources))) out = mergeByKey(out, byKey);

  for (const [month, byKey] of Object.entries(sources?.rollup?.months ?? {})) {
    if (!MONTH_RE.test(month)) continue;
    if (from != null && `${month}-01` < from) continue;
    if (to != null && monthEnd(month) > to) continue;
    out = mergeByKey(out, byKey);
  }

  return out;
}
